// SEO-Optimized PSEO data structure with keyword research for 2025
export const PSEO_GENRES = {
  "romance": {
    title: "Romance",
    description: "Create captivating romance novels readers love with AI-powered tools for Amazon KDP publishing",
    // High-volume, 2-3 word phrases per Amazon best practices
    keywords: ["romance book creator", "write romance novel", "KDP romance publishing", "romance book cover", "dark romance book", "enemies to lovers", "spicy romance", "paranormal romance", "contemporary romance", "romance novel generator"],
    tips: [
      "Focus on emotional connection and chemistry between characters",
      "Develop strong character tension with clear conflict", 
      "Include realistic dialogue and compelling intimate moments",
      "Target trending subgenres: dark romance, mafia, shifter romance"
    ],
    examples: ["Pride and Prejudice", "The Notebook", "Outlander", "It Ends with Us"],
    searchVolume: 5400,
    competition: "medium",
    coverTips: [
      "Use warm colors: reds, pinks, rose gold for romance appeal",
      "Feature attractive couples or solo models that match subgenre",
      "Include romantic elements: flowers, hearts, dramatic lighting",
      "Choose elegant, readable fonts that convey the mood"
    ],
    marketingTips: [
      "Target readers aged 25-45, primarily female audience",
      "Leverage BookTok and Instagram for visual marketing",
      "Partner with romance book bloggers and ARC readers",
      "Build series potential with interconnected characters"
    ]
  },
  
  "fantasy": {
    title: "Fantasy",
    description: "Build magical worlds and epic fantasy adventures that transport readers to new realms",
    keywords: ["fantasy book creator", "epic fantasy writing", "urban fantasy novel", "fantasy book cover", "magic system", "world building guide", "fantasy novel generator", "paranormal fantasy", "YA fantasy", "dragon fantasy book"],
    tips: [
      "Create consistent, immersive world-building with detailed lore",
      "Develop unique magic systems with clear rules and limitations",
      "Build compelling characters with meaningful growth arcs", 
      "Balance action sequences with character development and dialogue"
    ],
    examples: ["Lord of the Rings", "Harry Potter", "Game of Thrones", "Name of the Wind"],
    searchVolume: 4100,
    competition: "high",
    coverTips: [
      "Use rich, vibrant colors and magical visual elements",
      "Feature fantasy creatures, fae characters, or mystical landscapes",
      "Include weapons, magic symbols, or ancient artifacts",
      "Choose ornate, medieval-inspired fonts for epic fantasy"
    ],
    marketingTips: [
      "Target young adult and adult fantasy enthusiasts",
      "Emphasize unique world-building and magic system elements", 
      "Position within specific sub-genres: urban, epic, paranormal",
      "Build series potential for long-term reader loyalty"
    ]
  },

  "mystery": {
    title: "Mystery", 
    description: "Craft suspenseful mystery and thriller novels that keep readers guessing until the final page",
    keywords: ["mystery book creator", "thriller novel writing", "cozy mystery book", "detective story", "crime fiction writing", "mystery book cover", "whodunit mystery", "police procedural", "psychological thriller", "murder mystery"],
    tips: [
      "Plant compelling clues and effective red herrings throughout",
      "Create a complex but ultimately solvable puzzle for readers", 
      "Build suspense through expert pacing and strategic revelation",
      "Develop memorable detective or investigator protagonists"
    ],
    examples: ["Sherlock Holmes", "Agatha Christie novels", "Gone Girl", "The Girl with the Dragon Tattoo"],
    searchVolume: 3200,
    competition: "low",
    coverTips: [
      "Use dark, moody color schemes: blacks, deep blues, grays",
      "Include mysterious elements: shadows, silhouettes, fog", 
      "Feature subtle crime scene elements without being graphic",
      "Choose bold, dramatic fonts that convey tension"
    ],
    marketingTips: [
      "Target mystery and thriller enthusiasts, true crime fans",
      "Emphasize the puzzle and suspense in book descriptions",
      "Use intriguing taglines and questions in marketing",
      "Position clearly: cozy mystery vs. hard-boiled thriller"
    ]
  },

  "business": {
    title: "Business",
    description: "Share proven business strategies and entrepreneurship insights for aspiring and established leaders",
    keywords: ["business book creator", "entrepreneurship guide", "leadership book", "business strategy", "startup book", "digital business", "passive income", "business book cover", "small business guide", "executive leadership"],
    tips: [
      "Base all content on real business experience and verified data",
      "Include actionable frameworks and step-by-step strategies",
      "Use relevant case studies and concrete real-world examples",
      "Structure content with clear, implementable takeaways"
    ],
    examples: ["The Lean Startup", "Good to Great", "Think and Grow Rich", "The 7 Habits"],
    searchVolume: 2800,
    competition: "medium",
    coverTips: [
      "Use professional colors: navy blue, gray, corporate colors",
      "Keep design clean, authoritative, and trustworthy",
      "Include subtle business imagery: charts, skylines, minimalism",
      "Choose modern, bold fonts that convey authority"
    ],
    marketingTips: [
      "Target entrepreneurs, executives, and business professionals",
      "Emphasize credibility through author credentials and results",
      "Use LinkedIn and professional networks for promotion",
      "Position for niche business topics for better ranking"
    ]
  },

  "self-help": {
    title: "Self-Help",
    description: "Empower readers with transformative personal development and self-improvement strategies",
    keywords: ["self help book", "personal development", "self improvement guide", "mindfulness book", "habit formation", "mental health book", "motivation book", "wellness guide", "life coaching", "productivity book"],
    tips: [
      "Address specific, relatable problems with proven solutions",
      "Include evidence-based advice and actionable exercises",
      "Build credibility through research, credentials, or experience",
      "Structure with clear steps and measurable outcomes"
    ],
    examples: ["Atomic Habits", "The Power of Now", "You Are a Badass", "The Subtle Art of Not Giving a F*ck"],
    searchVolume: 4500,
    competition: "high",
    coverTips: [
      "Use uplifting colors: yellows, oranges, light blues",
      "Keep design clean and aspirational",
      "Include symbolic imagery: sunrise, paths, mountains",
      "Choose modern, readable fonts that inspire"
    ],
    marketingTips: [
      "Target readers seeking transformation and growth",
      "Use social proof: testimonials, transformation stories",
      "Leverage Instagram, Pinterest for visual content",
      "Partner with life coaches and wellness influencers"
    ]
  },

  "cookbook": {
    title: "Cookbook",
    description: "Create delicious recipe collections and culinary guides for food lovers and home cooks",
    keywords: ["cookbook creator", "recipe book", "keto cookbook", "healthy recipes", "meal prep guide", "diet cookbook", "baking book", "easy recipes", "food photography", "cooking for beginners"],
    tips: [
      "Focus on specific diet or cuisine niches for better targeting",
      "Include high-quality food photography for each recipe",
      "Test all recipes thoroughly for accuracy and clarity",
      "Provide clear instructions with ingredient substitutions"
    ],
    examples: ["Salt, Fat, Acid, Heat", "The Joy of Cooking", "Half Baked Harvest", "Magnolia Table"],
    searchVolume: 2200,
    competition: "low",
    coverTips: [
      "Use appetite-appealing food photography as hero image",
      "Choose warm, inviting colors that match cuisine type",
      "Include utensils or ingredients as design elements",
      "Use clean, elegant fonts for professional look"
    ],
    marketingTips: [
      "Target specific diet communities: keto, vegan, paleo",
      "Use Instagram and Pinterest for recipe teasers",
      "Partner with food bloggers and nutrition influencers",
      "Emphasize health benefits or ease of preparation"
    ]
  },

  "children": {
    title: "Children's Books",
    description: "Create engaging children's stories and educational books that inspire young readers",
    keywords: ["children book creator", "kids book", "picture book", "bedtime story", "educational book", "children coloring book", "early reader book", "chapter book", "toddler book", "activity book"],
    tips: [
      "Create age-appropriate content with suitable vocabulary",
      "Include colorful, engaging illustrations on every page",
      "Focus on educational value or positive life lessons",
      "Keep text concise and rhythm for read-aloud appeal"
    ],
    examples: ["The Very Hungry Caterpillar", "Where the Wild Things Are", "Goodnight Moon", "The Giving Tree"],
    searchVolume: 3800,
    competition: "medium",
    coverTips: [
      "Use bright, vibrant colors that attract children",
      "Feature appealing characters or animals prominently",
      "Include playful, whimsical design elements",
      "Choose fun, bold, readable fonts for young readers"
    ],
    marketingTips: [
      "Target parents, grandparents, and educators",
      "Emphasize educational value and age appropriateness",
      "Use Facebook parent groups and education forums",
      "Position for gift-giving occasions and holidays"
    ]
  },

  "biography": {
    title: "Biography",
    description: "Tell compelling life stories and memoirs that inspire and educate readers",
    keywords: ["biography book", "memoir writing", "life story book", "autobiography", "inspirational biography", "historical biography", "celebrity memoir", "true story", "personal narrative", "biography creator"],
    tips: [
      "Focus on inspiring, transformative life stories",
      "Conduct thorough research with verified sources",
      "Create emotional connection through authentic storytelling",
      "Structure chronologically or thematically for impact"
    ],
    examples: ["Steve Jobs", "Becoming", "The Diary of a Young Girl", "Long Walk to Freedom"],
    searchVolume: 1800,
    competition: "low",
    coverTips: [
      "Feature subject photo or symbolic imagery prominently",
      "Use sophisticated, timeless color palettes",
      "Keep design elegant and professional",
      "Choose classic, readable fonts that convey gravitas"
    ],
    marketingTips: [
      "Target readers interested in inspirational stories",
      "Emphasize unique aspects of the subject's life",
      "Use traditional media and book clubs for promotion",
      "Position within relevant historical or cultural context"
    ]
  }
};

export const PSEO_TOOLS = {
  "cover-generator": {
    title: "Cover Generator",
    description: "Create professional, eye-catching book covers that boost sales on Amazon KDP",
    benefits: [
      "Professional designs in minutes with AI assistance",
      "No graphic design experience required", 
      "Genre-optimized templates for better conversion",
      "High-resolution files ready for KDP upload"
    ]
  },
  "writing-guide": {
    title: "Writing Guide", 
    description: "Complete step-by-step guide to writing, publishing, and marketing your book successfully",
    benefits: [
      "Expert writing strategies for your specific genre",
      "Genre-specific techniques from bestselling authors",
      "Complete Amazon KDP publishing roadmap",
      "Proven marketing and promotion tactics"
    ]
  },
  "book-creator": {
    title: "Book Creator",
    description: "AI-powered book generator that creates complete manuscripts from your ideas",
    benefits: [
      "AI-generated content tailored to your genre",
      "Customizable chapters, scenes, and story structure",
      "Multiple genre options with specialized templates",
      "Professional formatting ready for publishing"
    ]
  },
  "publishing-guide": {
    title: "Publishing Guide",
    description: "Master Amazon KDP publishing with proven strategies for book sales and rankings",
    benefits: [
      "Complete step-by-step KDP publishing process",
      "SEO and keyword optimization strategies",
      "Advanced marketing and book promotion tactics",
      "Revenue maximization and pricing strategies"
    ]
  }
};

// Generate all possible PSEO combinations
export const generatePSEOPages = () => {
  const pages = [];
  const genres = Object.keys(PSEO_GENRES);
  const tools = Object.keys(PSEO_TOOLS);

  genres.forEach(genre => {
    tools.forEach(tool => {
      const genreData = PSEO_GENRES[genre];
      const toolData = PSEO_TOOLS[tool];
      
      pages.push({
        slug: `${genre}-book-${tool}`,
        title: `${genreData.title} ${toolData.title}`,
        description: `${toolData.description} for ${genre} books. ${genreData.description}`,
        genre,
        tool
      });
    });
  });

  return pages;
};

// Get related pages for internal linking
export const getRelatedPages = (currentGenre, currentTool, allPages) => {
  return allPages
    .filter(page => 
      (page.genre === currentGenre && page.tool !== currentTool) ||
      (page.tool === currentTool && page.genre !== currentGenre)
    )
    .slice(0, 6); // Limit to 6 related pages
};
